import { Box, Divider, Grid, Link, Paper } from '@mui/material';
import { Add, AddToDriveOutlined, BuildOutlined, Check, CloudOutlined, DoorBackOutlined, WebAssetOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { getSiteSubscription } from "../../../actions/internal/SiteActions";
import { getUserId } from "../../../actions/profile/UserActions";
import Colors from "../../../constants/Colors";
import { CARD_CAPTION_STYLE, CARD_HEADER_STYLE } from "../../../constants/Styles";
import { withRouter } from 'react-router';
import { observer } from 'mobx-react-lite';
import { ContainedButton } from '../../common/StyledButtons';
import StyledProgressCircle from '../../common/StyledProgressCircle';
import StyledSubHeader from '../../common/StyledSubHeader';
import { getSiteById, getSiteSubscriptionById } from '../../../selectors/SiteSelectors';
import StyledChip from "../../common/StyledChip";
import StyledAccordion from '../../common/StyledAccordion';
import { DesktopBox } from '../../common/Boxes';
import SiteToolsCard from './cards/SiteToolsCard';
import StyledPage from '../../common/StyledPage';
import StyledSection from '../../common/StyledSection';

export const SERVICE_ACTIONS = {
    SITE_CARE: {
        url: '/site-care',
        button: 'Request an Update',
        description: "Get updates or additions made to your site."
    },
    SITE_CARE_PLUS: {
        url: '/site-care',
        button: 'Request an Update',
        description: "Get updates or additions made to your site."
    },
    SHOP_BLOG: {
        url: '/shop-blog-editor',
        button: 'Request an Update',
        description: 'Expand your store with new items or enrich your blog with fresh posts.'
    }
}

const HOSTING_NO_PACKAGE_TITLE = "NO_PACKAGE"
const WEBMASTER_SERVICE_TITLE = 'WEBMASTER_SUPPORT';
const WEBMASTER_HAS_SERVICE_DESC = 'With our Proactive Website Care, BCK Consulting keeps your site secure, updated, and running smoothly so you can focus on your business.'
const WEBMASTER_NO_SERVICE_DESC = 'With a Self-Managed Site, you take full responsibility for all updates, enhancements, and security measures.'

const ViewSitePackageComponent = ({ history, location, match }) => {

    const [ isLoading, setIsLoading ] = useState(true);

    const siteId = match.params.site_id
    const subscription = getSiteSubscriptionById(siteId)
    const site = getSiteById(siteId);

    useEffect(() => {
        loadData().then();
    }, [])

    const loadData = async () => {
        try {
            const userId = getUserId()
            await getSiteSubscription(userId, siteId)
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const resolvePackageName = () => {
        if (!subscription || !subscription.site_package) return '---'
        return subscription.site_package.package_details.name;
    }

    const resolveHostingName = () => {
        if (!subscription || !subscription.site_package) return '---'
        return subscription.site_package.hosting_product.name;
    }

    const resolveHostingDescription = () => {
        if (!subscription || !subscription.site_package) return '---'
        return subscription.site_package.hosting_product.description;
    }

    const hasWebmasterService = () => {
        if (!subscription || !subscription.site_package) return false
        return !!subscription.site_package.package_services.included_services[WEBMASTER_SERVICE_TITLE] || !!subscription.site_package.package_services.extra_services[WEBMASTER_SERVICE_TITLE]
    }

    const hasHostingService = () => {
        if (!subscription || !subscription.site_package || !subscription.site_package.hosting_product.name) return false;
        return subscription.site_package.hosting_product.is_active;
    }

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderHostingDetails = () => {
        if (!hasHostingService()) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                    <Box>
                        <h6 style={{ textAlign: 'left', padding: 0, fontSize: '24px' }}>Hosting Not Managed by BCK</h6>
                        <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>This website is currently hosted outside of BCK’s managed environment. While we can still provide support, some features and optimizations may not be available. If you're interested in switching to BCK Hosting for better performance and security, let us know!</p>
                    </Box>
                </Box>
            )
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                <Box>
                    <h6 style={{ textAlign: 'left', padding: 0 }}>{resolveHostingName()}</h6>
                    <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>{resolveHostingDescription()}</p>
                </Box>
            </Box>
        )
    }

    const renderPerksSection = () => {
        if (!hasHostingService()) return [];
        return subscription.site_package.hosting_product.perks && subscription.site_package.hosting_product.perks.map(perk => (
            <Box key={perk.name} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginRight: '18px', marginBottom: '10px' }}>
                    <Check sx={{ color: Colors.primaryColor, fontSize: '24px' }} />
                </Box>
                <Box>
                    <h6 style={{ textAlign: 'left', padding: 0, fontSize: '20px', fontWeight: 'normal' }}>{perk.name}</h6>
                </Box>
            </Box>
        ))
    }

    const renderExtraServicesSection = () => {
        if (!hasHostingService() || subscription.site_package.extra_products.length === 0) return (
            <Box>
                <h6 style={{ textAlign: 'left', padding: 0, fontSize: '24px' }}>No Additional Services</h6>
                <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>There are currently no additional BCK services active on this account. If you’re interested in adding SEO, analytics, or support options, we’re happy to help.</p>
            </Box>
        );

        return (
            <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                {subscription.site_package.extra_products && subscription.site_package.extra_products.map((prod, index) => (
                    <Box key={prod.name}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', marginBottom: '10px', marginTop: '10px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginLeft: '9px', marginRight: '9px', marginBottom: '10px' }}>
                                <Add sx={{ color: Colors.orangeColor, fontSize: '24px' }} />
                            </Box>
                            <Box>
                                <h6 style={{ textAlign: 'left', padding: 0, fontSize: '20px' }}>{prod.name}</h6>
                                <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>{prod.description}</p>
                            </Box>
                        </Box>
                        {index !== subscription.site_package.extra_products.length - 1 && <Divider />}
                    </Box>
                ))}
            </Paper>
        )
    }
    
    const renderOverviewSection = () => (
        <StyledSection
            title="What Would You Like To Do?"
        >
            <Grid container item xs={12} spacing={1}>
                {renderActionCards()}
            </Grid>
        </StyledSection>
    )

    const renderActionCards = () =>
        prepareTools().map((tool) => (
        <Grid item xs={12} sm={6} md={4} key={tool.name}>
        <Paper
            variant="outlined"
            onClick={tool.onClick}
            sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'center',
            height: '100%',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            boxSizing: 'border-box',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transform: 'scale(1.02)',
            },
            }}
        >
            <Box sx={{ mb: 1.5 }}>
            {tool.icon}
            </Box>

            <Box>
            <div style={CARD_HEADER_STYLE}>{tool.name}</div>
            <p style={{ margin: 0 }}>{tool.description}</p>
            </Box>
        </Paper>
        </Grid>
    ));


  const prepareTools = () => {
    const tools = []
        if (site.site_external_connections.box) {
        tools.push({
            name: "Share a File",
            icon: <AddToDriveOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
            description: 'Securely view and share files with BCK, powered by Google Drive.',
            link: site.site_external_connections.box.box_url})
        }
        if (site.site_external_connections.wordpress) {
        tools.push({
            name: "Wordpress Dashboard",
            icon: <WebAssetOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
            description: 'Access to your website media and content.',
            link: site.site_external_connections.wordpress.wordpress_login_url})
        }
        tools.push({
        name: "Advanced Panel",
        icon: <DoorBackOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
        description: 'Backend access for your site & services.',
        link: 'https://d-90679ff33b.awsapps.com/start/'
        })
        
        return tools
    }

    return (
        <StyledPage>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                <StyledSubHeader title="Website" />
                </Grid>

                <Grid item xs={12}>
                {isLoading ? (
                    renderLoadingContent()
                ) : (
                    <>
                    {renderOverviewSection()}
                    </>
                )}
                </Grid>
            </Grid>
        </StyledPage>
    )
}

export default observer(withRouter(ViewSitePackageComponent));
